"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { Platform, resolvePlatformIcon } from "@/data/platforms";
import LeadCapturePopup from "@/components/forms/LeadCapturePopup";

interface PlatformHeroProps {
  platform: Platform;
  contactFormId?: string;
  /** Demo-popup copy. Defaults keep the ERP wording (Mx-ERP pages). */
  consultationHeadline?: string;
  consultationSubheadline?: string;
}

export function PlatformHero({ platform, contactFormId, consultationHeadline, consultationSubheadline }: PlatformHeroProps) {
  const Icon = resolvePlatformIcon(platform.icon);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <section
      style={{
        minHeight: "calc(100vh - var(--header-height, 64px))",
      }}
      className="relative flex flex-col items-center justify-center bg-white overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32"
    >
      {/* ── Background ── */}
      <div className="absolute inset-0 z-0 pointer-events-none select-none overflow-hidden">
        <Image
          src="/images/bg-img/bg-3.webp"
          alt="Background Pattern"
          fill
          priority
          className="object-cover opacity-[0.18] md:opacity-[0.25]"
        />

        {/* Floating brand blobs */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-blue-100/30 blur-[120px]"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-32 -left-32 h-[360px] w-[360px] rounded-full bg-indigo-100/25 blur-[120px]"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-white/40" />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center text-center px-4 sm:px-6 lg:px-8">
        {/* Badge */}
        <motion.div
          initial={{ y: -18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-5 md:mb-6 inline-flex items-center gap-2 rounded-full border border-[#237BC5]/20 bg-white/70 px-4 py-1.5 shadow-sm backdrop-blur-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#237BC5] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#237BC5]" />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#237BC5]">
            {platform.category}
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
           className="text-[1.75rem] font-black tracking-tight text-[#19609E] sm:text-[2.2rem] md:text-3xl lg:text-4xl leading-[1.1]"
        >
          {platform.heroHeadline}
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.6 }}
          className="mt-4 max-w-2xl text-base text-slate-600 md:text-lg leading-relaxed"
        >
          {platform.heroSubheadline}
        </motion.p>

        {/* Social proof */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-6 flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white/70 px-4 py-2.5 shadow-sm backdrop-blur-sm md:gap-5 md:px-6 md:py-3"
        >
          <div className="flex items-center gap-2 text-amber-400">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <span className="text-xs font-semibold text-slate-600 md:text-sm">
            Trusted by 1000+ Businesses
          </span>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24, duration: 0.7 }}
          className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:gap-4"
        >
          <button
            type="button"
            onClick={() => setIsPopupOpen(true)}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#237BC5] px-7 text-xs font-black uppercase tracking-normal text-white shadow-lg shadow-[#08265a]/25 transition-all hover:brightness-110 hover:scale-[1.02] w-full sm:w-auto"
          >
            Request Free Demo
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => {
              const el = document.getElementById(contactFormId || "contact-form");
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[#237BC5]/20 bg-white px-7 text-xs font-black uppercase tracking-normal text-[#237BC5] shadow-sm transition-all hover:border-[#237BC5]/40 hover:bg-[#237BC5]/5 w-full sm:w-auto"
          >
            Talk to an Expert
            <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>

      {/* ── Explore label ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="relative z-10 mt-10 md:mt-12"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
          Explore {platform.product} products
        </span>
      </motion.div>

      <LeadCapturePopup
        open={isPopupOpen}
        onOpenChange={setIsPopupOpen}
        triggerLabel="Request Free Demo"
        headline={consultationHeadline || "Schedule Your Free ERP Consultation"}
        subheadline={consultationSubheadline || "Explore how ACGIL's 30+ years of ERP expertise can streamline your operations. Our experts will walk you through a tailored solution."}
      />
    </section>
  );
}
